import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, Paperclip, Search, Hash, Users, ChevronDown } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeProjectMessages,
  sendProjectMessage,
  subscribeChatReads,
  markProjectChatRead,
  uploadChatAttachment,
  isChatUnread,
  countUnreadMessages,
} from '../../services/projectChat';
import './AdminPages.css';

function lastPreview(project) {
  if (!project?.lastChatText) return 'No messages yet';
  const who = project.lastChatSenderName ? `${project.lastChatSenderName}: ` : '';
  return `${who}${project.lastChatText}`;
}

export default function TeamChatPage() {
  const { data, showToast } = useApp();
  const { user, role } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const projects = useMemo(() => (data.projects || []).filter((p) => {
    if (role === 'employee') {
      return (p.team || []).map(String).includes(String(user?.employeeId));
    }
    return true;
  }), [data.projects, role, user?.employeeId]);

  const [activeChat, setActiveChat] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [chatReads, setChatReads] = useState({});
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return subscribeChatReads(user.uid, setChatReads);
  }, [user?.uid]);

  useEffect(() => {
    const fromUrl = searchParams.get('project');
    if (!projects.length) return;
    if (fromUrl && projects.some((p) => String(p.id) === String(fromUrl))) {
      setActiveChat(fromUrl);
      return;
    }
    setActiveChat((prev) => {
      const valid = projects.some((p) => String(p.id) === String(prev));
      if (!prev || !valid) return projects[0].id;
      return prev;
    });
  }, [projects, searchParams]);

  const selectProject = (projectId) => {
    setActiveChat(projectId);
    setSearchParams({ project: String(projectId) }, { replace: true });
  };

  useEffect(() => {
    if (!activeChat) return undefined;
    const unsub = subscribeProjectMessages(
      activeChat,
      (rows) => {
        setMessages(rows.map((m) => ({ ...m, own: m.senderUid === user?.uid })));
      },
      (err) => {
        const msg = err?.code === 'permission-denied'
          ? 'No permission to read this project chat.'
          : (err?.message || 'Could not load messages');
        showToast(msg, 'error');
      }
    );
    return () => unsub();
  }, [activeChat, user?.uid, showToast]);

  useEffect(() => {
    if (!activeChat || !user?.uid) return;
    markProjectChatRead(activeChat, user.uid).catch(() => {});
  }, [activeChat, user?.uid, messages.length]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, activeChat]);

  const send = async () => {
    if (!input.trim() || !activeChat || !user?.uid) return;
    const parts = (user.name || 'You').split(' ');
    const senderName = `${parts[0] || 'You'} ${(parts[parts.length - 1] || '')[0] || ''}.`.trim();
    try {
      await sendProjectMessage(activeChat, { senderUid: user.uid, senderName, text: input.trim() });
      setInput('');
    } catch {
      showToast('Could not send message', 'error');
    }
  };

  const sendFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChat || !user?.uid) return;
    const parts = (user.name || 'You').split(' ');
    const senderName = `${parts[0] || 'You'} ${(parts[parts.length - 1] || '')[0] || ''}.`.trim();
    setUploading(true);
    try {
      const { url, name } = await uploadChatAttachment(activeChat, file);
      await sendProjectMessage(activeChat, {
        senderUid: user.uid,
        senderName,
        text: input.trim() || `Shared ${name}`,
        attachmentUrl: url,
        attachmentName: name,
      });
      setInput('');
    } catch (err) {
      showToast(err?.message || 'Could not upload file', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const proj = projects.find((p) => String(p.id) === String(activeChat));
  const openUnread = countUnreadMessages(messages, chatReads[activeChat], user?.uid);

  if (!projects.length) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Team Chat</h1></div></div>
        <p style={{ color:'var(--text-muted)' }}>Create a project first to start team chats.</p>
      </div>
    );
  }

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
    || (p.lastChatText || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="page-header" style={{ marginBottom:16 }}>
        <div><h1 className="page-title">Team Chat</h1><p className="page-subtitle">Real-time project collaboration (Firestore)</p></div>
      </div>
      <div className="chat-layout">
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <div style={{ display:'flex',alignItems:'center',gap:7,marginBottom:10,fontWeight:700,fontSize:'0.875rem' }}>
              <Hash size={15} style={{ color:'var(--secondary-color)' }}/> Project Chats
            </div>
            <div className="search-box" style={{ minWidth:'unset' }}>
              <Search size={13}/>
              <input className="search-input" placeholder="Search projects or messages..." value={search} onChange={e=>setSearch(e.target.value)} style={{ fontSize:'0.8rem' }}/>
            </div>
          </div>
          <div className="chat-list">
            {filteredProjects.map((p) => {
              const isActive = String(activeChat) === String(p.id);
              const unread = isChatUnread({ project: p, readEntry: chatReads[p.id], userUid: user?.uid });
              return (
                <div key={p.id} className={`chat-item ${isActive ? 'active' : ''}`} onClick={() => selectProject(p.id)}>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:6 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:6,minWidth:0 }}>
                      <div style={{ width:7,height:7,borderRadius:'50%',background:p.status==='In Progress'?'var(--success)':p.status==='Review'?'var(--warning)':'var(--text-dim)',flexShrink:0 }}/>
                      <p className="chat-item-name">{p.name}</p>
                    </div>
                    {unread && <span className="chat-unread-dot" title="Unread"/>}
                  </div>
                  <p className="chat-item-last">{lastPreview(p)}</p>
                  {p.lastChatTimeLabel && <p className="chat-item-time">{p.lastChatTimeLabel}</p>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="chat-main">
          <div className="chat-header">
            <div className="chat-header-mobile">
              <select className="form-input chat-mobile-select" value={activeChat} onChange={e=>selectProject(e.target.value)}>
                {projects.map((p) => {
                  const hasUnread = isChatUnread({ project: p, readEntry: chatReads[p.id], userUid: user?.uid });
                  return (
                    <option key={p.id} value={p.id}>{p.name}{hasUnread ? ' • unread' : ''}</option>
                  );
                })}
              </select>
              <ChevronDown size={14} className="chat-mobile-chevron"/>
            </div>
            <div className="chat-header-title">
              <p style={{ fontWeight:700,fontSize:'0.95rem' }}>{proj?.name}</p>
              <div style={{ display:'flex',alignItems:'center',gap:5,fontSize:'0.75rem',color:'var(--text-muted)',flexWrap:'wrap' }}>
                <Users size={12}/>{(proj?.team||[]).length} members · {proj?.client}
                {openUnread > 0 && <span className="chat-unread-pill">{openUnread} unread</span>}
              </div>
            </div>
            <div className="chat-header-avatars" style={{ display:'flex',gap:4 }}>
              {(proj?.team||[]).slice(0,3).map((id) => {
                const emp = data.employees.find((e) => String(e.id) === String(id));
                return <div key={id} className="av av-sm av-gold" title={emp?.name}>{emp?.avatar||'?'}</div>;
              })}
            </div>
          </div>

          <div className="chat-messages">
            <div style={{ fontSize:'0.72rem', color:'var(--text-dim)', marginBottom:10, textAlign:'center' }}>
              {messages.length} message{messages.length === 1 ? '' : 's'} in this project thread
            </div>
            {messages.length === 0 && (
              <div style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-dim)',fontSize:'0.875rem' }}>
                No messages yet. Start the conversation!
              </div>
            )}
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-msg ${msg.own ? 'own' : 'other'}`}>
                <div className="chat-bubble">
                  {msg.text && <span>{msg.text}</span>}
                  {msg.attachmentUrl && (
                    <a href={msg.attachmentUrl} target="_blank" rel="noreferrer" className="chat-attachment-link">
                      {msg.attachmentName || 'Download attachment'}
                    </a>
                  )}
                </div>
                <div className="chat-meta">{!msg.own && <span>{msg.sender}</span>}<span>{msg.time}</span></div>
              </div>
            ))}
            <div ref={bottomRef}/>
          </div>

          <div className="chat-input-row">
            <input ref={fileRef} type="file" hidden accept="image/*,.pdf,.doc,.docx,.txt" onChange={sendFile}/>
            <button type="button" className="btn-ghost" style={{ width:38,height:38,padding:0,display:'flex',alignItems:'center',justifyContent:'center' }} onClick={()=>fileRef.current?.click()} disabled={uploading}>
              <Paperclip size={15}/>
            </button>
            <input
              className="chat-input"
              placeholder={`Message ${proj?.name || ''}…`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              disabled={uploading}
            />
            <button className="chat-send" onClick={send} disabled={!input.trim() || uploading}><Send size={15}/></button>
          </div>
        </div>
      </div>
    </div>
  );
}
